/**
 * Composable for managing page titles
 * Provides a centralized way to set document titles for all pages
 */

import { watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const DEFAULT_TITLE = 'Kaizen Platform'

/**
 * Set the page title
 * @param title - The title to set (only adds "Kaizen Platform" if title is empty)
 * @param includeAppName - Whether to include "Kaizen Platform" when title is empty (default: true)
 */
export function setPageTitle(title: string, includeAppName: boolean = true): void {
  // If title is provided and not empty, use it as-is
  if (title && title.trim()) {
    document.title = title
  } else {
    // Only show "Kaizen Platform" if title is empty
    document.title = includeAppName ? DEFAULT_TITLE : ''
  }
}

/**
 * Composable to manage page titles with automatic cleanup
 * @param title - Initial title or function that returns title
 * @param includeAppName - Whether to include app name prefix
 */
export function usePageTitle(
  title: string | (() => string) | null = null,
  includeAppName: boolean = true
) {
  const route = useRoute()
  
  // Set initial title if provided
  if (title) {
    const titleValue = typeof title === 'function' ? title() : title
    setPageTitle(titleValue, includeAppName)
  }
  
  // Watch for route changes and reset to default if needed
  const unwatch = watch(
    () => route.path,
    () => {
      // Only reset if no title is provided or title function returns null
      if (!title || (typeof title === 'function' && !title())) {
        setPageTitle('', includeAppName)
      }
    }
  )
  
  // Cleanup watcher on unmount
  onUnmounted(() => {
    unwatch()
  })
  
  return {
    setTitle: (newTitle: string, includePrefix: boolean = includeAppName) => {
      setPageTitle(newTitle, includePrefix)
    },
    resetTitle: () => {
      setPageTitle('', includeAppName)
    }
  }
}

/**
 * Format title for detail pages
 * @param type - Type of item (e.g., 'Test Run', 'Bug', 'Feature')
 * @param id - ID of the item (e.g., 'TR00001', 'B00001', 'F00001')
 * @param name - Optional name/title of the item
 */
export function formatDetailTitle(type: string, id: string, name?: string): string {
  if (name) {
    return `${id} - ${name}`
  }
  return id
}

